import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Zap, Timer, Gift, TrendingUp, Check, Sparkles, ShieldCheck,
} from "lucide-react";

function fmtMxn(n) { return "$" + Number(n || 0).toLocaleString("es-MX"); }

/**
 * Fase 13.4 · Página post-compra con la mejor oferta upsell.
 * Ruta: `/checkout/upsell/:orderId`. Si no hay match → redirect a success.
 *
 * `copy_variant` sólo cambia el énfasis visual del layout:
 *  * urgency → countdown ámbar visible + copy escaso
 *  * value   → savings destacado (naranja primary por brand)
 *  * bonus   → "+" bonus con gift icon
 *
 * NOTE: usa tokens semánticos (primary / destructive / muted) — el
 * rebrand naranja+negro+blanco se controla desde tailwind + CSS vars.
 */
export default function UpsellOffer() {
  const { orderId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [upsell, setUpsell] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`/checkout/upsell/${orderId}`)}`);
      return;
    }
    // Guard contra StrictMode double-mount: sólo un fetch por orderId.
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    api.get(`/checkout/upsells/next?order_id=${orderId}`)
      .then((r) => {
        setUpsell(r.data.upsell);
        setCountdown(r.data.upsell?.countdown_seconds || 0);
      })
      .catch(() => setUpsell(null))
      .finally(() => setLoading(false));
  }, [orderId, user, authLoading, nav]);

  useEffect(() => {
    if (countdown <= 0) return;
    const iv = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(iv);
  }, [countdown]);

  // Fase 13.4 · si no hay upsell disponible → redirect a la orden (en effect,
  // no durante render, para evitar warning "setState during render")
  useEffect(() => {
    if (!loading && !upsell) {
      nav(`/app/alumno/ordenes/${orderId}`, { replace: true });
    }
  }, [loading, upsell, orderId, nav]);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/checkout/upsells/${upsell.id}/accept`, { order_id: orderId });
      toast.success(r.data.message || "Añadido a tu cuenta");
      const r2 = await api.get(`/checkout/upsells/next?order_id=${orderId}`);
      if (r2.data.upsell) {
        setUpsell(r2.data.upsell);
        setCountdown(r2.data.upsell.countdown_seconds || 0);
        setBusy(false);
      } else {
        nav(`/app/alumno/ordenes/${orderId}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const reject = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/checkout/upsells/${upsell.id}/reject`, { order_id: orderId });
      const r2 = await api.get(`/checkout/upsells/next?order_id=${orderId}`);
      if (r2.data.upsell) {
        setUpsell(r2.data.upsell);
        setCountdown(r2.data.upsell.countdown_seconds || 0);
        setBusy(false);
      } else {
        nav(`/app/alumno/ordenes/${orderId}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (loading || !upsell) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto py-24 px-4 text-center text-muted-foreground">
          Cargando…
        </div>
      </PublicLayout>
    );
  }

  const mm = Math.floor(countdown / 60);
  const ss = String(countdown % 60).padStart(2, "0");
  const variant = upsell.copy_variant;
  const savings = upsell.original_price_mxn - upsell.price_mxn;

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success confirmation ribbon */}
        <div
          className="p-4 rounded-xl bg-primary/5 border border-primary/30 flex items-start gap-3 mb-6"
          data-testid="upsell-success-ribbon"
        >
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <Check className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">¡Compra confirmada!</p>
            <p className="text-xs text-muted-foreground">
              Antes de irte, tenemos una oferta exclusiva para ti.
            </p>
          </div>
        </div>

        {/* Upsell card — el copy_variant sólo cambia el ribbon del header */}
        <article
          data-testid="upsell-card"
          className={`relative overflow-hidden rounded-2xl border-2 p-8 shadow-2xl ${
            variant === "urgency"
              ? "border-destructive bg-destructive/5"
              : "border-primary bg-primary/5"
          }`}
        >
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {variant === "urgency" && countdown > 0 && (
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold uppercase tracking-widest"
                data-testid="upsell-countdown"
              >
                <Timer className="w-3 h-3 animate-pulse" /> Expira en {mm}:{ss}
              </span>
            )}
            {variant === "value" && savings > 0 && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-widest">
                <TrendingUp className="w-3 h-3" /> Ahorras {fmtMxn(savings)}
              </span>
            )}
            {variant === "bonus" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-foreground text-background text-[11px] font-bold uppercase tracking-widest">
                <Gift className="w-3 h-3" /> Bonus exclusivo
              </span>
            )}
            {upsell.discount_pct > 0 && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-widest">
                <Zap className="w-3 h-3" /> {upsell.discount_pct}% OFF
              </span>
            )}
          </div>

          {upsell.headline && (
            <p className="text-xs uppercase tracking-widest font-bold text-primary mb-2">
              {upsell.headline}
            </p>
          )}
          <h1 className="text-3xl md:text-4xl font-extrabold text-foreground font-display leading-tight">
            {upsell.title}
          </h1>
          {upsell.description && (
            <p className="mt-4 text-foreground/80">{upsell.description}</p>
          )}

          <div className="mt-6 flex items-baseline gap-3 flex-wrap">
            <span
              className="text-5xl font-extrabold text-foreground tabular-nums"
              data-testid="upsell-price"
            >
              {fmtMxn(upsell.price_mxn)}
            </span>
            {upsell.original_price_mxn > upsell.price_mxn && (
              <span className="text-xl text-muted-foreground line-through font-mono">
                {fmtMxn(upsell.original_price_mxn)}
              </span>
            )}
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              MXN · pago único
            </span>
          </div>

          <div className="mt-8 space-y-3">
            <button
              type="button"
              onClick={accept}
              disabled={busy}
              data-testid="upsell-accept-btn"
              className={`w-full py-4 rounded-md font-bold text-base transition-all hover:scale-[1.01] shadow-lg disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2 ${
                variant === "urgency"
                  ? "bg-destructive text-destructive-foreground hover:brightness-110"
                  : "bg-primary text-primary-foreground hover:brightness-110"
              }`}
            >
              <Sparkles className="w-5 h-5" /> Sí, agregar por {fmtMxn(upsell.price_mxn)} · 1 click
            </button>
            <button
              type="button"
              onClick={reject}
              disabled={busy}
              data-testid="upsell-reject-btn"
              className="w-full py-3 rounded-md border border-border text-muted-foreground hover:bg-muted font-semibold text-sm disabled:opacity-60"
            >
              No gracias, continuar
            </button>
          </div>

          <p className="mt-4 text-[10px] uppercase tracking-widest text-center text-muted-foreground font-bold flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Pago mock · sin cargo real
          </p>
        </article>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link
            to="/app/alumno"
            data-testid="upsell-skip-all"
            className="hover:text-foreground underline underline-offset-2"
          >
            Saltar y ver mi cuenta →
          </Link>
        </p>
      </div>
    </PublicLayout>
  );
}
